<template>
  <div class="password-settings-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" color="#fff">mdi-shield-lock</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">{{ $t("password_settings") }}</h1>
          <p class="header-subtitle">Keep your account secure with a strong password</p>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <v-card class="password-card" elevation="0">
      <v-card-text class="card-content" ref="passContent">
        <!-- Password Fields -->
        <div class="password-fields">
          <!-- Old Password -->
          <div class="field-container">
            <label class="field-label">Current Password</label>
            <v-text-field
              v-model="oldPassword"
              outlined
              dense
              placeholder="Enter your current password"
              :error-messages="oldPassMessage"
              :rules="[rules.required, rules.min]"
              :type="oldShowPassword ? 'text' : 'password'"
              :append-icon="oldShowPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="oldShowPassword = !oldShowPassword"
              @input="onChange"
              @focus="currentStep = 1"
              ref="oldPassword"
              class="password-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey" size="20">mdi-lock-outline</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- New Password -->
          <div class="field-container">
            <label class="field-label">New Password</label>
            <v-text-field
              v-model="newPassword"
              outlined
              dense
              placeholder="Create a strong password"
              :error-messages="newPassMessage"
              :rules="[rules.required, rules.min]"
              :type="newShowPassword ? 'text' : 'password'"
              :append-icon="newShowPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="newShowPassword = !newShowPassword"
              @input="onChange"
              @focus="currentStep = 2"
              ref="newPassword"
              class="password-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey" size="20">mdi-lock-plus-outline</v-icon>
              </template>
            </v-text-field>
            
            <!-- Password Strength Indicator -->
            <div class="password-strength" v-if="newPassword">
              <div class="strength-bar">
                <div class="strength-fill" :class="passwordStrength.class" :style="{ width: passwordStrength.width }"></div>
              </div>
              <span class="strength-text" :class="passwordStrength.class">{{ passwordStrength.text }}</span>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="field-container">
            <label class="field-label">Confirm New Password</label>
            <v-text-field
              v-model="confirmPassword"
              outlined
              dense
              placeholder="Confirm your new password"
              :error-messages="confirmMessage"
              :rules="[rules.required, rules.min]"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword = !showPassword"
              @input="onChange"
              @focus="currentStep = 3"
              ref="confirmPassword"
              class="password-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey" size="20">mdi-lock-check-outline</v-icon>
              </template>
            </v-text-field>
          </div>
        </div>

        <!-- Password Requirements -->
        <div class="requirements-section">
          <h3 class="requirements-title">Password Requirements</h3>
          <div class="requirements-grid">
            <div class="requirement-item" :class="{ met: newPassword.length >= 8 }">
              <v-icon size="16" :color="newPassword.length >= 8 ? 'success' : 'grey'">
                {{ newPassword.length >= 8 ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>At least 8 characters</span>
            </div>
            <div class="requirement-item" :class="{ met: hasUpperCase }">
              <v-icon size="16" :color="hasUpperCase ? 'success' : 'grey'">
                {{ hasUpperCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>One uppercase letter</span>
            </div>
            <div class="requirement-item" :class="{ met: hasLowerCase }">
              <v-icon size="16" :color="hasLowerCase ? 'success' : 'grey'">
                {{ hasLowerCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>One lowercase letter</span>
            </div>
            <div class="requirement-item" :class="{ met: hasNumber }">
              <v-icon size="16" :color="hasNumber ? 'success' : 'grey'">
                {{ hasNumber ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>One number</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="card-actions">
        <v-spacer></v-spacer>
        <v-btn
          text
          color="grey"
          @click="resetForm"
          :disabled="loading"
          class="cancel-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSavePassword"
          :loading="loading"
          :disabled="!isFormValid"
          class="save-btn"
          elevation="2"
        >
          <v-icon left size="18">mdi-content-save</v-icon>
          Update Password
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="5000"
      bottom
      right
      class="custom-snackbar"
    >
      <v-icon left color="white">{{ snackbarIcon }}</v-icon>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          color="white"
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "PasswordSettings",
  data() {
    return {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      errorMessage: null,
      oldPassMessage: null,
      newPassMessage: null,
      confirmMessage: null,
      loading: false,
      oldShowPassword: false,
      newShowPassword: false,
      showPassword: false,
      currentStep: 1,
      rules: {
        required: value => !!value || "Required.",
        min: v => (v && v.length >= 8) || "Min 8 characters",
        passwordMatch: () =>
          this.newPassword != this.confirmPassword
            ? `Password doesn't match`
            : ""
      },
      snackbar: false,
      snackMessage: null,
      snackbarColor: "success",
      snackbarIcon: "mdi-check-circle"
    };
  },
  computed: {
    passwordStrength() {
      const password = this.newPassword;
      if (!password) return { class: '', width: '0%', text: '' };
      
      let score = 0;
      if (password.length >= 8) score++;
      if (/[A-Z]/.test(password)) score++;
      if (/[a-z]/.test(password)) score++;
      if (/[0-9]/.test(password)) score++;
      if (/[^A-Za-z0-9]/.test(password)) score++;
      
      const strength = {
        0: { class: 'weak', width: '20%', text: 'Very Weak' },
        1: { class: 'weak', width: '20%', text: 'Weak' },
        2: { class: 'fair', width: '40%', text: 'Fair' },
        3: { class: 'good', width: '60%', text: 'Good' },
        4: { class: 'strong', width: '80%', text: 'Strong' },
        5: { class: 'very-strong', width: '100%', text: 'Very Strong' }
      };
      
      return strength[score] || strength[0];
    },
    hasUpperCase() {
      return /[A-Z]/.test(this.newPassword);
    },
    hasLowerCase() {
      return /[a-z]/.test(this.newPassword);
    },
    hasNumber() {
      return /[0-9]/.test(this.newPassword);
    },
    isFormValid() {
      return this.oldPassword && 
             this.newPassword && 
             this.confirmPassword && 
             this.newPassword === this.confirmPassword &&
             this.newPassword.length >= 8;
    }
  },
    watch: {
    newPassword() {
      this.validateConfirmPassword();
    },
    confirmPassword() {
      this.validateConfirmPassword();
    }
  },
  methods: {
    ...mapActions("usersetting", {
      changePassword: "changePassword"
    }),
        validateConfirmPassword() {
      if (this.confirmPassword && this.newPassword !== this.confirmPassword) {
        this.confirmMessage = "Password does not match";
      } else {
        this.confirmMessage = null;
      }
    },
    onSavePassword() {
      if (!this.oldPassword) {
        this.oldPassMessage = "Old password is required";
        this.$vuetify.goTo(
          this.$refs.oldPassword,
          {
            container: this.$refs.passContent
          },
          500
        );
        return;
      }

      if (!this.newPassword) {
        this.newPassMessage = "Required.";
        return;
      }
      if (!this.confirmPassword) {
        this.confirmMessage = "Required.";
        return;
      }
      if (this.newPassword != this.confirmPassword) {
        this.confirmMessage = "Password does not match";
        return;
      }
      
      this.loading = true;
      this.changePassword({
        oldPassword: this.oldPassword,
        newPassword: this.newPassword
      })
        .then(() => {
          this.newPassword = "";
          this.confirmPassword = "";
          this.oldPassword = "";
          this.showSnackbar("Password changed successfully!", "success", "mdi-check-circle");
          this.loading = false;
          this.currentStep = 1;
        })
        .catch(error => {
          if (error.response.data.passwordError) {
            this.oldPassMessage = error.response.data.passwordError;
          }
          if (error.response.data.message) {
            this.errorMessage = error.response.data.message;
            this.showSnackbar(error.response.data.message, "error", "mdi-alert-circle");
            this.loading = false;
          } else {
            this.errorMessage = error;
            this.showSnackbar("An error occurred. Please try again.", "error", "mdi-alert-circle");
            this.loading = false;
          }
        });
    },
    onChange() {
      this.errorMessage = null;
      this.oldPassMessage = null;
      this.newPassMessage = null;
      this.confirmMessage = null;
    },
    showSnackbar(message, color = "success", icon = "mdi-check-circle") {
      this.snackMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon;
      this.snackbar = true;
    },
    resetForm() {
      this.oldPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.currentStep = 1;
      this.onChange();
    }
  }
};
</script>

<style scoped>
.password-settings-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  min-width : 90%
}

.header-section {
  margin-bottom: 32px;
  position: relative;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  padding: 2rem 1rem 3rem;
  overflow: hidden;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  border: 2px solid #fff;
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 28px;
  font-weight: 700;
  color: white !important;
  margin: 0 0 4px 0;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  /* -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; */
  background-clip: text;
}

.header-subtitle {
  font-size: 16px;
  color: #ffff;
  margin: 0;
}

.password-card {
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-content {
  padding: 32px;
}

.steps-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  padding: 0 20px;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.step-item.active {
  opacity: 1;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #64748b;
  transition: all 0.3s ease;
}

.step-item.active .step-circle {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  color: white;
  transform: scale(1.1);
}

.step-text {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-line {
  width: 60px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 16px;
  transition: background 0.3s ease;
}

.step-line.active {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
}

.password-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.password-input {
  transition: all 0.3s ease;
}

.password-input:focus-within {
  transform: translateY(-2px);
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.strength-bar {
  flex: 1;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 2px;
}

.strength-fill.weak {
  background: #ef4444;
}

.strength-fill.fair {
  background: #f59e0b;
}

.strength-fill.good {
  background: #3b82f6;
}

.strength-fill.strong {
  background: #10b981;
}

.strength-fill.very-strong {
  background: #059669;
}

.strength-text {
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.strength-text.weak {
  color: #ef4444;
}

.strength-text.fair {
  color: #f59e0b;
}

.strength-text.good {
  color: #3b82f6;
}

.strength-text.strong {
  color: #10b981;
}

.strength-text.very-strong {
  color: #059669;
}

.requirements-section {
  margin-top: 32px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.requirements-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #64748b;
  transition: color 0.3s ease;
}

.requirement-item.met {
  color: #059669;
}

.card-actions {
  padding: 24px 32px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn {
  text-transform: none;
  font-weight: 500;
}

.save-btn {
  text-transform: none;
  font-weight: 600;
  padding: 0 24px;
  border-radius: 8px;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.save-btn:disabled {
  opacity: 0.6;
  transform: none;
}

.custom-snackbar {
  border-radius: 8px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .password-settings-container {
    padding: 16px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .header-icon {
    width: 50px;
    height: 50px;
  }
  
  .header-title {
    font-size: 24px;
  }
  
  .card-content {
    padding: 24px;
  }
  
  .steps-container {
    padding: 0 10px;
  }
  
  .step-line {
    width: 40px;
    margin: 0 8px;
  }
  
  .requirements-grid {
    grid-template-columns: 1fr;
  }
  
  .card-actions {
    padding: 16px 24px;
    flex-direction: column;
    gap: 12px;
  }
  
  .save-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .steps-container {
    flex-direction: column;
    gap: 16px;
  }
  
  .step-line {
    width: 2px;
    height: 30px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .card-content {
    padding: 20px;
  }
}
</style>